import { useEffect, useRef, useState } from 'react'

const THRESHOLD = 72   // px pulled before we trigger
const RESISTANCE = 2.5 // how much drag slows the pull

export function usePullToRefresh(onRefresh: () => Promise<void> | void) {
  const startY = useRef(0)
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const pulling = useRef(false)

  useEffect(() => {
    const el = document.querySelector('.app-content') as HTMLElement | null
    if (!el) return

    function onTouchStart(e: TouchEvent) {
      // Only activate when scrolled to top
      if (el!.scrollTop > 0) return
      startY.current = e.touches[0].clientY
      pulling.current = true
    }

    function onTouchMove(e: TouchEvent) {
      if (!pulling.current || refreshing) return
      const delta = e.touches[0].clientY - startY.current
      if (delta <= 0) {
        setPullDistance(0)
        return
      }
      // Prevent browser bounce/scroll so our indicator shows
      e.preventDefault()
      setPullDistance(Math.min(delta / RESISTANCE, THRESHOLD * 1.5))
    }

    async function onTouchEnd() {
      if (!pulling.current) return
      pulling.current = false
      if (pullDistance >= THRESHOLD && !refreshing) {
        setRefreshing(true)
        try {
          await onRefresh()
        } finally {
          setRefreshing(false)
        }
      }
      setPullDistance(0)
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [onRefresh, refreshing, pullDistance])

  return { pullDistance, refreshing, triggered: pullDistance >= THRESHOLD }
}
